<template>
    <div v-if="loaded">
        <rkm-form
            ref="Form"
            show-legend
        >
            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-select
                        :options="form.unidades"
                        :validations="validations.unidade"
                        @change="model.infraestruturaId = model.centroCustoId = model.statusId = null"
                        disabled
                        label="Unidade"
                        show-default-option
                        text-field="nomeFantasia"
                        v-model="model.unidadeId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-select
                        :options="infraestruturas"
                        :validations="validations.infraestrutura"
                        @change="model.centroCustoId = model.statusId = null"
                        disabled
                        label="Local de atendimento"
                        show-default-option
                        text-field="nome"
                        v-model="model.infraestruturaId"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-select
                        :options="centrosCusto"
                        :validations="validations.centroCusto"
                        @change="model.statusId = (status.find(s => s.tipoId === enums.status.EM_ESPERA) || {}).id || null"
                        label="Setor"
                        show-default-option
                        text-field="setorNome"
                        v-model="model.centroCustoId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-select
                        :options="status"
                        :validations="validations.status"
                        label="Status"
                        show-default-option
                        text-field="nome"
                        v-model="model.statusId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="auto">
                    <rkm-button
                        :disabled="!model.statusId"
                        :label="false"
                        @click="$refs.Form.validate(search)"
                        color="primary"
                    >
                        Buscar
                    </rkm-button>
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <rkm-row>
            <rkm-col>
                <rkm-table
                    :fields="fields"
                    :items="fila"
                    :tbody-tr-attr="handleRowAttr"
                    ref="Table"
                >
                    <template v-slot:cell(tempoEspera)="row">
                        <rkm-icon
                            v-if="row.item.tempoEspera.id"
                            :id="uniqueId('tempo-espera-tooltip')"
                            :style="'color: ' + row.item.tempoEspera.cor"
                            :title="handleTempoEspera(row.item.tempoEspera.total)"
                            font-scale="2"
                            icon="battery-full"
                            v-rkm-tooltip.hover
                        />

                        <span v-else>
                            -
                        </span>

                        <slot
                            name="tempo-espera"
                            v-bind:item="row.item"
                        />
                    </template>

                    <template v-slot:cell(prioridade)="row">
                        {{ row.item.prioridade.nome }}

                        <rkm-icon
                            v-if="row.item.atendimento.recepcao.urgente"
                            :title="row.item.atendimento.recepcao.urgenteMotivo"
                            class="ml-2"
                            color="red"
                            icon="exclamation-triangle"
                            v-rkm-tooltip.hover
                        />
                    </template>

                    <template v-slot:cell(idade)="row">
                        <template v-if="row.item.atendimento.paciente.cidadao.hasIdade">
                            {{ row.item.atendimento.paciente.cidadao.idade.anos }} {{ row.item.atendimento.paciente.cidadao.idade.anos > 1 ? 'anos' : 'ano' }},
                            {{ row.item.atendimento.paciente.cidadao.idade.meses }} {{ row.item.atendimento.paciente.cidadao.idade.meses > 1 ? 'meses' : 'mês ' }} e
                            {{ row.item.atendimento.paciente.cidadao.idade.dias }} {{ row.item.atendimento.paciente.cidadao.idade.dias > 1 ? 'dias' : 'dia' }}
                        </template>

                        <template v-else>
                            Data de nascimento não preenchida
                        </template>

                    </template>

                    <template v-slot:cell(nome)="{ item }">
                        <rkm-button
                            :id="uniqueId(`paciente-icon-${item.id}`)"
                            class="mr-2"
                            small
                        >
                            <rkm-icon icon="user"/>
                        </rkm-button>

                        <rkm-popover
                            :target="uniqueId(`paciente-icon-${item.id}`)"
                            triggers="hover"
                            placement="top"
                            title="Dados cadastrais"
                        >
                            <div>
                                <span>
                                    <strong>Data de nascimento:</strong> {{ item.atendimento.paciente.cidadao.dataNascimento }}
                                </span>
                            </div>

                            <div>
                                <span>
                                    <strong>Nome da mãe:</strong> {{ item.atendimento.paciente.cidadao.nomeMae }}
                                </span>
                            </div>
                        </rkm-popover>

                        <span
                            :id="uniqueId(`paciente-nome-${item.id}`)"
                            :class="{ 'text-primary': item.atendimento.paciente.isDesconhecido }"
                        >
                            {{ item.atendimento.paciente.cidadao.nome }}

                            <template v-if="item.atendimento.paciente.cidadao.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                                <strong>
                                    ({{ item.atendimento.paciente.cidadao.nomeSocial }})
                                </strong>
                            </template>
                        </span>

                        <rkm-popover
                            v-if="item.atendimento.paciente.isDesconhecido"
                            :target="uniqueId(`paciente-nome-${item.id}`)"
                            triggers="hover"
                            placement="top"
                            title="Características físicas"
                        >
                            <span>
                                {{ item.atendimento.paciente.cidadao.caracteristicasFisicas }}
                            </span>
                        </rkm-popover>
                    </template>

                    <template v-slot:cell(actions)="{ item }">
                        <rkm-button
                            @click="$refs.Modal.open(item.id, item.prioridade.id)"
                            color="primary"
                            small
                            title="Editar"
                        >
                            <rkm-icon icon="pen" />
                        </rkm-button>
                    </template>
                </rkm-table>
            </rkm-col>
        </rkm-row>

        <rkm-row class="mt-2">
            <rkm-col>
                <p class="m-0">
                    Pacientes em espera: {{ totalEmEspera }}
                </p>

                <p class="m-0">
                    Pacientes em atendimento: {{ totalEmAtendimento }}
                </p>

                <p class="m-0">
                    Linhas em <span class="warning">amarelo</span> representam pacientes na fila por mais de 24 horas.
                </p>
            </rkm-col>
        </rkm-row>

        <Modal
            :form="form"
            @saved="saved"
            ref="Modal"
        />
    </div>
</template>

<script>
// Others.
import { repository, error } from '@/mixins'
import { mapActions } from 'vuex'
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import status from '@/enums/painel-senha/status'
import Modal from './components/prioridade/Modal'

export default {
    components: {Modal},
    computed: {
        validations() {

            return {
                unidade: { required: true },
                infraestrutura: { required: true },
                centroCusto: { required: true },
                status: { required: true },
            }
        },
        infraestruturas() {

            if (this.model.unidadeId) {

                return this.form.unidades.find(u => u.id === this.model.unidadeId).infraestruturas
            }

            return []
        },
        centrosCusto() {

            if (this.model.infraestruturaId && this.infraestruturas.length > 0) {

                return this.infraestruturas.find(i => i.id === this.model.infraestruturaId).centrosCusto
            }

            return []
        },
        setorTipoId() {

            if (this.model.centroCustoId && this.centrosCusto.length > 0) {

                return this.centrosCusto.find(cc => cc.id === this.model.centroCustoId).setorTipoId
            }

            return null
        },
        status() {

            if (this.model.centroCustoId && this.centrosCusto.length > 0) {

                return this.centrosCusto.find(i => i.id === this.model.centroCustoId).status
            }

            return []
        },
        statusId() {

            if (this.model.statusId && this.status.length > 0) {

                const status = this.status.find(s => s.id === this.model.statusId)

                return status ? status.tipoId : null
            }

            return null
        },
        fields() {

            if (this.statusId === this.enums.status.EM_ATENDIMENTO) {

                return [
                    { key: 'opcaoEntrada.nome', label: 'Opção de Entrada' },
                    { key: 'senha', label: 'Senha' },
                    { key: 'prioridade', label: 'Prioridade' },
                    { key: 'atendimento.recepcao.atendidoEm', label: 'Data do Atendimento' },
                    { key: 'atendimento.paciente.cidadao.prontuario', label: 'Prontuário' },
                    { key: 'nome', label: 'Nome' },
                    { key: 'Idade', label: 'Idade' },
                    { key: 'atendimento.recepcao.ocupacao.nome', label: 'Especialidade Médica' },
                    { key: 'atendimento.profissionalAtual.nome', label: 'Profissional' },
                    { key: 'actions', label: 'Ações', class: 'col-min text-center' },
                ]
            }
            else {

                return [
                    { key: 'tempoEspera', label: 'Tempo de espera', class: 'col-min' },
                    { key: 'opcaoEntrada.nome', label: 'Opção de Entrada' },
                    { key: 'senha', label: 'Senha' },
                    { key: 'prioridade', label: 'Prioridade' },
                    { key: 'atendimento.recepcao.atendidoEm', label: 'Data do Atendimento' },
                    { key: 'atendimento.paciente.cidadao.prontuario', label: 'Prontuário' },
                    { key: 'nome', label: 'Nome' },
                    { key: 'Idade', label: 'Idade' },
                    { key: 'atendimento.recepcao.ocupacao.nome', label: 'Especialidade Médica' },
                    { key: 'actions', label: 'Ações', class: 'col-min text-center' },
                ]
            }
        },
    },
    created() {

        this.repositories = {
            filaAtendimento: this.getConectaSusRepository('prontoAtendimento.filaAtendimento')
        }

        this.load()
    },
    data: () => ({
        additionals: null,
        form: null,
        model: null,
        loaded: false,
        fila: [],
        totalEmEspera: 0,
        totalEmAtendimento: 0,
        enums: { status, preferenciaNome },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            this.repositories.filaAtendimento.list()
                .then(({ data }) => {

                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model

                    this.loaded = true

                    this.$watch('model', { deep: true, handler: () => {

                        this.fila = []
                        this.totalEmAtendimento = 0
                        this.totalEmEspera = 0
                    }})
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        search() {

            this.setLoading()

            this.repositories.filaAtendimento.index({
                centro_custo_id: this.model.centroCustoId,
                setor_tipo_id: this.setorTipoId,
                status_id: this.model.statusId,
            })
                .then(({ data }) => {

                    this.fila = data.fila
                    this.totalEmEspera = data.totalEmEspera
                    this.totalEmAtendimento = data.totalEmAtendimento
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleRowAttr(item, type) {

            if (type === 'row') {

                if (item.atendimento.classificacaoRisco.tipo.cor) {

                    return {
                        style: `
                            border-left: 10px solid ${item.atendimento.classificacaoRisco.tipo.cor};
                            background-color: ${item.atendimento.classificacaoRisco.tipo.cor}15;
                        `
                    }
                }
            }
        },
        handleTempoEspera(value) {
            let format = 'Esperando por'

            if (value.dias !== null) {
                format = `${format} ${value.dias} dia(s)`
            }

            if (value.horas !== null) {
                format = `${format} ${value.horas} hora(s)`
            }

            if (value.minutos !== null) {
                if (value.horas !== null) {
                    format = `${format} e ${value.minutos} minuto(s)`
                } else {
                    format = `${format} ${value.minutos} minuto(s)`
                }
            }

            return format
        },
        saved() {
            this.search()
        },
    },
    mixins: [repository, error],
    watch: {
        'fila'(value) {
            value.map(item => {
                if (item.destacado) {
                    item._rowVariant = 'warning'
                }

                return item
            })
        },
    },
}
</script>

<style scoped>
.warning {
    color: var(--warning);
}
</style>